import './knowledge-app.scss';
import Swiper from 'swiper';
import {A11y, Autoplay, Keyboard} from 'swiper/modules';

/**
 * KnowledgeApp
 *
 * Handles the interactive slider of the Knowledge App section.
 * Provides autoplay functionality with pause/play controls.
 */
class KnowledgeApp {
    /**
     * Creates an instance of KnowledgeApp.
     * @param {HTMLElement} element - Root element of the Knowledge App section.
     */
    constructor(element) {
        /**
         * Root element of the Knowledge App section.
         * @type {HTMLElement}
         */
        this.$knowledgeApp = element;

        /**
         * Slider container element.
         * @type {HTMLElement|null}
         */
        this.$knowledgeAppSlider = this.$knowledgeApp.querySelector('[data-knowledge-slider="slider"]');

        /**
         * All individual slide elements.
         * @type {NodeListOf<HTMLElement>}
         */
        this.$knowledgeAppSlides = this.$knowledgeApp.querySelectorAll('[data-knowledge-slider="slide"]');

        /**
         * @type {HTMLButtonElement}
         *
         */
        this.$knowledgeAppButton = this.$knowledgeApp.querySelector('[data-knowledge-slider-button]');
    }

    /**
     * Initializes the KnowledgeApp slider if there are multiple slides.
     */
    initialize() {
        if (this.$knowledgeAppSlides.length > 1) {
            this.initSlider();
        }
    }

    /**
     * Initializes the Swiper slider and the Pause/Play button functionality.
     * Autoplay stops automatically after 5 seconds if the user has not interacted.
     */
    initSlider() {
        if (this.$knowledgeAppSlider) {
            /** @type {Swiper} */
            const swiperInstance = new Swiper(this.$knowledgeAppSlider, {
                modules: [A11y, Autoplay, Keyboard],
                autoplay: {delay: 3000},
                loop: true,
                simulateTouch: false,
                slidesPerView: 'auto'
            });

            const btn = this.$knowledgeAppButton;
            if (!btn) return;
            const pauseLabel = btn.dataset?.labelPause;
            const playLabel = btn.dataset?.labelPlay;
            let isPlaying = true;

            // Toggle autoplay on button click
            btn.addEventListener('click', () => {
                if (isPlaying) {
                    swiperInstance.autoplay.stop();
                    btn.textContent = playLabel;
                } else {
                    swiperInstance.autoplay.start();
                    btn.textContent = pauseLabel;
                }
                isPlaying = !isPlaying;
            });

            // Stop autoplay automatically after 5 seconds if not manually paused
            setTimeout(() => {
                if (isPlaying) {
                    swiperInstance.autoplay.stop();
                    btn.textContent = playLabel;
                    isPlaying = false;
                }
            }, 5000);
        }
    }
}

export {KnowledgeApp};

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $knowledgeAppSlider = $context.querySelectorAll('[data-knowledge-slider="root"]');

        if ($knowledgeAppSlider.length > 0) {
            for (let i = 0; i < $knowledgeAppSlider.length; i++) {
                const knowledgeAppSlider = new KnowledgeApp($knowledgeAppSlider[i]);
                knowledgeAppSlider.initialize();
            }
        }
    }
});
